import { Container, Graphics, Sprite, Text, TextStyle, Texture } from 'pixi.js';
import type { Application } from 'pixi.js';
import { hexCss, type Palette } from '../palette/palettes';
import { createBody, type Body } from '../physics/Body';
import { PhysicsWorld, type PhysicsTuning } from '../physics/PhysicsWorld';
import { attractionForce, torqueFromPointer, repulsionForce, type AttractionTuning } from '../physics/pointerForce';
import { sampleAlignment, softFieldForces, ambientReceiverPull, type AlignmentTuning } from '../physics/alignment';
import { createGrainOverlay, resizeGrainOverlay } from '../render/grainTexture';
import {
  setCustomCursorClientPos,
  setCustomCursorCoupled,
  setCustomCursorIntensity,
} from '../ui/customCursor';

/** Must match @font-face in styles.css — quoted for canvas 2D. */
const MARK_FONT_FAMILY = '"BebasRegular", Impact, Haettenschweiler, "Arial Narrow Bold", sans-serif';

type BgNameMotif = {
  sprite: Sprite;
  vx: number;
  vy: number;
  rotVel: number;
  baseScale: number;
  driftPhase: number;
  pulseRate: number;
};

export type AlignTuning = AttractionTuning &
  PhysicsTuning &
  AlignmentTuning & {
    playerSizeMin: number;
    playerSizeMax: number;
    receiverScale: number;
    splitRatio: number;
    snapDuration: number;
    snapHold: number;
    receiverOscDeg: number;
    receiverOscPeriod: number;
    pointerVelSmooth: number;
    /** Seconds between antagonist circle spawns. */
    repulsorInterval: number;
    repulsorLife: number;
    repulsorRadius: number;
    repulsorStrength: number;
  };

export const DEFAULT_ALIGN_TUNING: AlignTuning = {
  attraction: 2490,
  maxForce: 2480,
  interactionRadius: 250,
  innerSoftRadius: 28,
  peakForceDistance: 72,
  torqueStrength: 1.45,

  linearDamping: 0.85,
  angularDamping: 0.45,
  restitution: 0.28,
  maxSpeed: 780,
  maxAngular: 6.5,

  snapDistanceRatio: 0.2,
  snapAngleDeg: 16,
  softFieldRatio: 0.48,
  softAngleDeg: 32,
  softPull: 710,
  softDamp: 3.5,
  ambientPull: 95,

  playerSizeMin: 56,
  playerSizeMax: 76,
  receiverScale: 1.3,
  splitRatio: 0.58,

  snapDuration: 0.34,
  snapHold: 0.12,
  receiverOscDeg: 9,
  receiverOscPeriod: 6,
  pointerVelSmooth: 0.22,

  repulsorInterval: 3.6,
  repulsorLife: 2.2,
  repulsorRadius: 110,
  repulsorStrength: 2100,
};

type Repulsor = {
  x: number;
  y: number;
  age: number;
  life: number;
  radius: number;
  strength: number;
};

export type AlignSceneEvents = {
  onSolved: (originX: number, originY: number, angle: number) => void;
};

export type AlignDebugState = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  speed: number;
  angle: number;
  angularVelocity: number;
  centerDist: number;
  angleDeltaDeg: number;
  physicsState: 'FREE' | 'FIELD' | 'SNAP' | 'DONE';
  force01: number;
  steps: number;
  paletteId: string;
};

export class AlignScene {
  readonly root = new Container();
  private fieldA = new Graphics();
  private fieldB = new Graphics();
  private grain = createGrainOverlay();
  private markLayer = new Container();
  private mark = new Text({ text: 'STEELE.IO', style: new TextStyle({ fill: 0xffffff }) });
  private bgNameLayer = new Container();
  private bgNameMotifs: BgNameMotif[] = [];
  private bgNameTexture: Texture | null = null;
  private bgNameRegion = { w: 1, h: 1 };
  private markFontsReady = false;
  private playerGfx = new Graphics();
  private receiverGfx = new Graphics();
  private repulsorGfx = new Graphics();
  private cursorGfx = new Graphics();
  private body!: Body;
  private receiver = {
    x: 0,
    y: 0,
    angle: 0,
    size: 80,
    baseAngle: 0,
    baseX: 0,
    baseY: 0,
  };
  private world: PhysicsWorld;
  private repulsors: Repulsor[] = [];
  private repulsorSpawnT = 1.2;
  private pointer = {
    x: 0,
    y: 0,
    active: false,
    isTouch: false,
    inside: false,
    svx: 0,
    svy: 0,
    lastTs: 0,
  };
  private lastForce01 = 0;
  private smoothPull01 = 0;
  private lastAlign = sampleAlignment(0, 0, 0, 0, 0, 0, 80, DEFAULT_ALIGN_TUNING);
  private solved = false;
  private snapping = false;
  private snapT = 0;
  private holdT = 0;
  private holding = false;
  private snapFrom = { x: 0, y: 0, angle: 0 };
  private hintShown = false;
  private interacted = false;
  private idleMs = 0;
  private reducedMotion = false;
  private w = 1;
  private h = 1;
  private landscape = true;
  private placed = false;
  private sessionSeed: number;
  private palette: Palette;
  /** 1 = settled and can fit; 0 = too swollen from rough handling. */
  private settle01 = 1;
  private calmT = 0;
  private isSettling = false;
  private readonly snapSettleMin = 0.58;
  private snapRejectCooldown = 0;
  /** Center of mass above geometric center (fraction of half-size). */
  private readonly topHeavyCom = 0.36;
  private rejectFlash = 0;
  private repulsorFlash = 0;
  private markHome = { x: 0, y: 0 };
  private markVel = { x: 0, y: 0 };
  private markRotVel = 0;
  private markIntroActive = false;
  private markIntroPending = true;
  private markIntroT = 0;
  private markIntroFrom = { x: 0, y: 0, rot: 0 };
  private readonly markIntroDur = 0.68;
  /** Smoothed 0–1 — ramps attraction so the mark never snaps when pink crosses. */
  private markAttract01 = 0;
  private markDrag = { active: false, offsetX: 0, offsetY: 0, pointerId: -1 };

  constructor(
    private readonly app: Application,
    palette: Palette,
    public tuning: AlignTuning,
    private readonly events: AlignSceneEvents,
    private readonly hintEl: HTMLElement | null,
    private readonly guideEl: HTMLElement | null = document.getElementById('align-guide'),
  ) {
    this.palette = palette;
    this.tuning.splitRatio = palette.splitRatio;
    this.world = new PhysicsWorld(tuning);
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.sessionSeed = hashSeed(palette.id);

    this.markLayer.addChild(this.mark);
    // Difference ≈ CSS mix-blend-mode: difference — inverts against fields.
    this.mark.blendMode = 'difference';
    this.mark.eventMode = 'none';

    this.bgNameLayer.eventMode = 'none';

    this.root.addChild(
      this.fieldA,
      this.bgNameLayer,
      this.fieldB,
      this.grain,
      this.markLayer,
      this.repulsorGfx,
      this.receiverGfx,
      this.playerGfx,
      this.cursorGfx,
    );
    this.app.stage.addChild(this.root);
    this.bootstrapBodies();
    this.bindPointer();
    void this.ensureMarkFonts().then(() => {
      if (this.w > 1) this.layoutMarkAndBgName();
    });
  }

  destroy(): void {
    this.unbindPointer();
    this.root.destroy({ children: true });
  }

  get snapSize(): number {
    return this.receiver.size;
  }

  /** Hot-swap palette (debug NEXT PALETTE). */
  applyPalette(palette: Palette): void {
    this.palette = palette;
    this.tuning.splitRatio = palette.splitRatio;
    this.sessionSeed = hashSeed(palette.id);
    this.drawStatic();
    this.drawDynamic();
  }

  resize(w: number, h: number): void {
    const wasLandscape = this.landscape;
    this.w = w;
    this.h = h;
    this.landscape = w >= h * 0.92;
    if (this.placed && wasLandscape !== this.landscape) {
      this.placed = false;
      this.resetMotionSoft();
    }
    this.layoutBodiesKeepRelative();
    this.drawStatic();
    if (this.markIntroPending) {
      this.beginMarkIntro();
      this.markIntroPending = false;
    } else if (this.markIntroActive) {
      this.markIntroFrom = this.markOffscreenStart();
    } else if (this.playerSplitCross01() <= 0.001) {
      this.resetMarkMotion();
    } else {
      this.updateMarkMotion(0);
    }
  }

  resetScene(): void {
    this.solved = false;
    this.snapping = false;
    this.holding = false;
    this.snapT = 0;
    this.holdT = 0;
    this.hintShown = false;
    this.interacted = false;
    this.idleMs = 0;
    this.placed = false;
    this.repulsors = [];
    this.repulsorSpawnT = 1.2;
    this.settle01 = 1;
    this.calmT = 0;
    this.isSettling = false;
    this.snapRejectCooldown = 0;
    this.rejectFlash = 0;
    this.repulsorFlash = 0;
    this.hideHint();
    this.guideEl?.classList.remove('is-dismissed');
    this.bootstrapBodies();
    this.layoutBodiesKeepRelative();
    this.drawStatic();
    this.beginMarkIntro();
    this.drawDynamic();
  }

  update(dt: number): void {
    this.updateBgNameMotion(dt);
    if (this.solved && !this.holding) return;

    if (this.holding) {
      this.holdT += dt;
      this.updateMarkMotion(dt);
      this.drawDynamic();
      const hold = this.reducedMotion ? 0.05 : this.tuning.snapHold;
      if (this.holdT >= hold) {
        this.holding = false;
        this.events.onSolved(this.receiver.x, this.receiver.y, this.receiver.angle);
      }
      return;
    }

    if (this.snapping) {
      this.updateSnap(dt);
      this.updateMarkMotion(dt);
      this.drawDynamic();
      return;
    }

    this.idleMs += dt * 1000;
    if (!this.interacted && !this.hintShown && this.idleMs > 5000) {
      this.showHint('→');
      this.hintShown = true;
    }

    const bodies = [this.body];
    if (this.pointer.active && this.pointer.inside && !this.markDrag.active) {
      const touchBoost = this.pointer.isTouch && this.w < 720 ? 1.4 : 1;
      const pullTuning =
        touchBoost === 1
          ? this.tuning
          : {
              ...this.tuning,
              interactionRadius: this.tuning.interactionRadius * touchBoost,
              peakForceDistance: this.tuning.peakForceDistance * 1.15,
            };
      const force = attractionForce(this.body.x, this.body.y, this.pointer.x, this.pointer.y, pullTuning);
      let fx = force.fx;
      let fy = force.fy;
      let strength01 = force.strength01;
      if (this.pointer.isTouch && this.lastAlign.inSoftField) {
        const softDist = this.receiver.size * this.tuning.softFieldRatio;
        if (this.lastAlign.centerDist <= softDist * 0.8) {
          const calmPull = 0.42 + (this.lastAlign.centerDist / softDist) * 0.35;
          fx *= calmPull;
          fy *= calmPull;
          strength01 *= calmPull;
        }
      }
      this.lastForce01 = strength01;

      const torque = torqueFromPointer(
        this.body.x,
        this.body.y,
        this.pointer.x,
        this.pointer.y,
        this.pointer.svx,
        this.pointer.svy,
        this.tuning.torqueStrength * (this.pointer.isTouch && this.lastAlign.inSoftField ? 0.55 : 1),
      );
      this.applyLinearForceWithTopHeavy(fx, fy, this.world.fixedDt);
      this.body.angularVelocity += (torque / (this.body.mass * 36)) * this.world.fixedDt;

      if (force.strength01 > 0.1 || Math.hypot(this.body.vx, this.body.vy) > 55) {
        this.markInteracted();
      }
    } else {
      this.lastForce01 = 0;
      this.pointer.svx *= 0.85;
      this.pointer.svy *= 0.85;
    }

    this.updateReceiverMotion();
    this.updateRepulsors(dt);

    // Antagonist circles push the square away.
    for (const r of this.repulsors) {
      const lifeT = r.age / r.life;
      const envelope = lifeT < 0.15 ? lifeT / 0.15 : lifeT > 0.75 ? (1 - lifeT) / 0.25 : 1;
      const pulse = repulsionForce(
        this.body.x,
        this.body.y,
        r.x,
        r.y,
        r.radius,
        r.strength * envelope,
      );
      this.applyLinearForceWithTopHeavy(pulse.fx, pulse.fy, this.world.fixedDt);
      if (pulse.strength01 > 0.12) {
        this.settle01 = Math.max(
          0,
          this.settle01 - pulse.strength01 * 0.42 * this.world.fixedDt,
        );
        this.repulsorFlash = Math.max(
          this.repulsorFlash,
          pulse.strength01 * (pulse.strength01 > 0.35 ? 0.95 : 0.55),
        );
      }
    }

    const align = sampleAlignment(
      this.body.x,
      this.body.y,
      this.body.angle,
      this.receiver.x,
      this.receiver.y,
      this.receiver.angle,
      this.receiver.size,
      this.tuning,
      this.snapping,
      this.solved,
    );
    this.lastAlign = align;

    // Always-on whisper toward the receiver so the destination is felt.
    if (!align.inSoftField) {
      const ambient = ambientReceiverPull(
        this.body.x,
        this.body.y,
        this.receiver.x,
        this.receiver.y,
        this.tuning,
      );
      this.applyLinearForceWithTopHeavy(ambient.fx, ambient.fy, this.world.fixedDt);
    }

    if (align.inSoftField) {
      const soft = softFieldForces(
        this.body.x,
        this.body.y,
        this.body.angle,
        this.receiver.x,
        this.receiver.y,
        this.receiver.angle,
        this.receiver.size,
        this.tuning,
      );
      this.applyLinearForceWithTopHeavy(soft.fx, soft.fy, this.world.fixedDt);
      this.body.angularVelocity += soft.torque * this.world.fixedDt;
      this.body.vx *= Math.exp(-soft.damp * this.world.fixedDt);
      this.body.vy *= Math.exp(-soft.damp * this.world.fixedDt);
      this.body.angularVelocity *= Math.exp(-soft.damp * this.world.fixedDt);

      // Extra calm near the receiver so settle can complete before snap.
      const softDist = this.receiver.size * this.tuning.softFieldRatio;
      const close = align.centerDist <= softDist * 0.75;
      const spd = Math.hypot(this.body.vx, this.body.vy);
      if (close && spd < 220) {
        const holding = this.pointer.active && this.pointer.inside;
        const dampPow = holding ? 8 : 5;
        const holdDamp = Math.exp(-dampPow * dt);
        this.body.vx *= holdDamp;
        this.body.vy *= holdDamp;
        this.body.angularVelocity *= Math.exp(-(holding ? 6 : 4) * dt);
      }
    }

    this.applyTopHeavySplitFall(dt);
    this.updateSettle(dt);
    this.snapRejectCooldown = Math.max(0, this.snapRejectCooldown - dt);

    if (align.canSnap) {
      const settleMin = this.pointer.isTouch ? 0.52 : this.snapSettleMin;
      if (this.settle01 >= settleMin) {
        this.beginSnap();
      } else if (this.snapRejectCooldown <= 0 && this.settle01 < 0.4) {
        this.applySnapReject(settleMin);
      }
    }

    this.rejectFlash = Math.max(0, this.rejectFlash - dt * 3.5);
    this.repulsorFlash = Math.max(0, this.repulsorFlash - dt * 5.5);
    const pullTarget =
      this.pointer.active && this.pointer.inside && !this.markDrag.active ? this.lastForce01 : 0;
    this.smoothPull01 += (pullTarget - this.smoothPull01) * Math.min(1, dt * 14);
    this.world.step(dt, bodies, { x: 0, y: 0, width: this.w, height: this.h });
    this.updateMarkMotion(dt);
    this.drawDynamic();
  }

  forceComplete(): void {
    if (this.solved || this.snapping || this.holding) return;
    this.beginSnap();
  }

  get debugStats(): AlignDebugState {
    const b = this.body;
    return {
      x: b.x,
      y: b.y,
      vx: b.vx,
      vy: b.vy,
      speed: Math.hypot(b.vx, b.vy),
      angle: b.angle,
      angularVelocity: b.angularVelocity,
      centerDist: this.lastAlign.centerDist,
      angleDeltaDeg: (this.lastAlign.angleDelta * 180) / Math.PI,
      physicsState: this.holding || this.solved ? 'DONE' : this.snapping ? 'SNAP' : this.lastAlign.physicsState,
      force01: this.lastForce01,
      steps: this.world.stepsLastFrame,
      paletteId: this.palette.id,
    };
  }

  private bootstrapBodies(): void {
    const size = this.playerSize();
    const rnd = mulberry32(this.sessionSeed ^ 0x9e3779b9);
    const speed = this.reducedMotion ? 28 : 48 + rnd() * 36;
    const dir = rnd() * Math.PI * 2;
    const spin = (this.reducedMotion ? 0.15 : 0.35) * (rnd() > 0.5 ? 1 : -1) * (0.7 + rnd() * 0.6);

    this.body = createBody({
      x: 0,
      y: 0,
      width: size,
      height: size,
      mass: 1,
      vx: Math.cos(dir) * speed,
      vy: Math.sin(dir) * speed * 0.7,
      angle: (rnd() - 0.5) * 0.9,
      angularVelocity: spin,
      restitution: this.tuning.restitution,
    });
  }

  private resetMotionSoft(): void {
    this.body.vx *= 0.4;
    this.body.vy *= 0.4;
    this.body.angularVelocity *= 0.5;
  }

  private playerSize(): number {
    const base = Math.min(this.w || window.innerWidth, this.h || window.innerHeight);
    const t = Math.min(1, Math.max(0, (base - 360) / 900));
    return this.tuning.playerSizeMin + (this.tuning.playerSizeMax - this.tuning.playerSizeMin) * t;
  }

  private layoutBodiesKeepRelative(): void {
    const size = this.playerSize();
    const prevW = Math.max(1, this.body.width);
    this.body.width = size;
    this.body.height = size;
    this.receiver.size = size * this.tuning.receiverScale;

    const split = this.landscape ? this.tuning.splitRatio : 0.55;

    if (!this.placed) {
      const rnd = mulberry32(this.sessionSeed ^ ((this.w * 13) | 0) ^ ((this.h * 7) | 0));
      if (this.landscape) {
        const splitX = this.w * split;
        this.body.x = splitX * (0.32 + rnd() * 0.22);
        this.body.y = this.h * (0.38 + rnd() * 0.28);
        this.receiver.baseX = splitX + (this.w - splitX) * (0.48 + rnd() * 0.12);
        this.receiver.baseY = this.h * (0.42 + rnd() * 0.16);
        this.receiver.baseAngle = ((rnd() - 0.5) * Math.PI) / 7;
      } else {
        const splitY = this.h * split;
        this.body.x = this.w * (0.28 + rnd() * 0.44);
        this.body.y = splitY * (0.34 + rnd() * 0.24);
        this.receiver.baseX = this.w * (0.42 + rnd() * 0.18);
        this.receiver.baseY = splitY + (this.h - splitY) * (0.48 + rnd() * 0.14);
        this.receiver.baseAngle = ((rnd() - 0.5) * Math.PI) / 8;
      }
      const toRx = this.receiver.baseX - this.body.x;
      const toRy = this.receiver.baseY - this.body.y;
      const toR = Math.hypot(toRx, toRy) || 1;
      const toward = (this.body.vx * toRx + this.body.vy * toRy) / toR;
      if (toward > 20) {
        this.body.vx -= (toRx / toR) * toward * 0.55;
        this.body.vy -= (toRy / toR) * toward * 0.55;
      }
      this.receiver.x = this.receiver.baseX;
      this.receiver.y = this.receiver.baseY;
      this.placed = true;
    } else if (this.landscape) {
      const splitX = this.w * split;
      this.receiver.baseX = splitX + (this.w - splitX) * 0.55;
      this.receiver.baseY = this.h * 0.48;
    } else {
      const splitY = this.h * split;
      this.receiver.baseX = this.w * 0.52;
      this.receiver.baseY = splitY + (this.h - splitY) * 0.55;
    }

    const scale = size / prevW;
    this.body.vx *= scale;
    this.body.vy *= scale;
    this.clampBody();
  }

  private clampBody(): void {
    const hw = this.body.width * 0.5;
    const hh = this.body.height * 0.5;
    this.body.x = Math.min(this.w - hw, Math.max(hw, this.body.x));
    this.body.y = Math.min(this.h - hh, Math.max(hh, this.body.y));
  }

  private updateReceiverMotion(): void {
    const t = performance.now() / 1000;
    const softDist = this.receiver.size * this.tuning.softFieldRatio;
    const nearHole = Math.hypot(this.body.x - this.receiver.x, this.body.y - this.receiver.y) <= softDist;
    const oscScale = nearHole ? 0.22 : 1;
    const oscAmp =
      ((this.reducedMotion ? 0.25 : 1) * this.tuning.receiverOscDeg * oscScale * Math.PI) / 180;
    const period = Math.max(2, this.tuning.receiverOscPeriod);
    this.receiver.angle = this.receiver.baseAngle + Math.sin((t * Math.PI * 2) / period) * oscAmp;
    this.receiver.x = this.receiver.baseX;
    this.receiver.y = this.receiver.baseY;
  }

  private drawStatic(): void {
    this.fieldA.clear();
    this.fieldB.clear();
    const split = this.landscape ? this.tuning.splitRatio : 0.55;
    if (this.landscape) {
      const splitX = this.w * split;
      this.fieldA.rect(0, 0, splitX, this.h).fill(this.palette.fieldA);
      this.fieldB.rect(splitX, 0, this.w - splitX, this.h).fill(this.palette.fieldB);
    } else {
      const splitY = this.h * split;
      this.fieldA.rect(0, 0, this.w, splitY).fill(this.palette.fieldA);
      this.fieldB.rect(0, splitY, this.w, this.h - splitY).fill(this.palette.fieldB);
    }
    resizeGrainOverlay(this.grain, this.w, this.h);
    if (this.markFontsReady) {
      this.layoutMarkAndBgName();
    } else {
      void this.ensureMarkFonts().then(() => {
        if (this.w > 1) this.layoutMarkAndBgName();
      });
    }
  }

  private async ensureMarkFonts(): Promise<void> {
    const markSize = Math.max(24, Math.min(this.w || 800, this.h || 600) * 0.05);
    const patternSize = Math.max(14, markSize * 0.32);
    await Promise.all([
      document.fonts.load(`${markSize}px ${MARK_FONT_FAMILY}`),
      document.fonts.load(`${patternSize}px ${MARK_FONT_FAMILY}`),
    ]);
    this.markFontsReady = true;
  }

  private layoutMarkAndBgName(): void {
    this.layoutMark();
    this.layoutBgName();
  }

  private markFontSize(): number {
    return Math.max(24, Math.min(this.w, this.h) * 0.05);
  }

  private markTextStyle(fill: number): TextStyle {
    return new TextStyle({
      fill,
      fontFamily: MARK_FONT_FAMILY,
      fontSize: this.markFontSize(),
      fontWeight: '400',
      letterSpacing: 6,
    });
  }

  /** Lifted ink on field A — dark ghost, still readable. */
  private ghostNameFill(): number {
    return this.palette.subtle;
  }

  private layoutMark(): void {
    this.mark.text = 'STEELE.IO';
    this.mark.style = this.markTextStyle(0xffffff);
    this.mark.anchor.set(0.5, 0.5);
    this.markHome.x = this.w * 0.5;
    this.markHome.y = this.h * 0.5;
    this.mark.alpha = 1;
    this.mark.blendMode = 'difference';
  }

  private clearBgNameLayer(): void {
    for (const motif of this.bgNameMotifs) {
      this.bgNameLayer.removeChild(motif.sprite);
      motif.sprite.destroy();
    }
    this.bgNameMotifs = [];
    if (this.bgNameTexture) {
      this.bgNameTexture.destroy(true);
      this.bgNameTexture = null;
    }
  }

  private updateBgNameMotion(dt: number): void {
    if (this.bgNameMotifs.length === 0) return;

    const motion = this.reducedMotion ? 0.25 : 1;
    const t = performance.now() / 1000;
    const rw = this.bgNameRegion.w;
    const rh = this.bgNameRegion.h;
    const pad = 48;

    for (const motif of this.bgNameMotifs) {
      const s = motif.sprite;
      s.x += motif.vx * motion * dt;
      s.y += motif.vy * motion * dt;
      s.rotation += motif.rotVel * motion * dt;

      if (s.x < -pad) s.x += rw + pad * 2;
      else if (s.x > rw + pad) s.x -= rw + pad * 2;
      if (s.y < -pad) s.y += rh + pad * 2;
      else if (s.y > rh + pad) s.y -= rh + pad * 2;

      const pulse = 1 + 0.07 * Math.sin(t * motif.pulseRate + motif.driftPhase);
      s.scale.set(motif.baseScale * pulse);
    }
  }

  private createBgNameTexture(fontSize: number): Texture | null {
    const letterSpacing = 6;
    const text = 'MICHAEL';
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const font = `${fontSize}px ${MARK_FONT_FAMILY}`;

    const probe = document.createElement('canvas').getContext('2d');
    if (!probe) return null;
    probe.font = font;

    let textW = 0;
    for (let i = 0; i < text.length; i += 1) {
      textW += probe.measureText(text[i]!).width;
      if (i < text.length - 1) textW += letterSpacing;
    }

    const pad = 12;
    const cssW = Math.ceil(textW + pad * 2);
    const cssH = Math.ceil(fontSize * 1.35 + pad);

    const canvas = document.createElement('canvas');
    canvas.width = cssW * dpr;
    canvas.height = cssH * dpr;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    ctx.scale(dpr, dpr);
    ctx.font = font;
    ctx.fillStyle = hexCss(this.ghostNameFill());
    ctx.globalAlpha = 0.88;
    ctx.textBaseline = 'middle';

    let x = pad;
    const y = cssH / 2;
    for (let i = 0; i < text.length; i += 1) {
      const ch = text[i]!;
      ctx.fillText(ch, x, y);
      x += probe.measureText(ch).width + letterSpacing;
    }

    return Texture.from(canvas);
  }

  private layoutBgName(): void {
    this.clearBgNameLayer();

    const patternFontSize = Math.max(14, this.markFontSize() * 0.32);
    const texture = this.createBgNameTexture(patternFontSize);
    if (!texture) return;
    this.bgNameTexture = texture;

    const split = this.landscape ? this.tuning.splitRatio : 0.55;
    const regionW = this.landscape ? this.w * split : this.w;
    const regionH = this.landscape ? this.h : this.h * split;
    this.bgNameRegion.w = regionW;
    this.bgNameRegion.h = regionH;

    const count = 340;
    const rand = mulberry32(this.sessionSeed ^ 0x6d696368);
    const speed = this.reducedMotion ? 0.45 : 1;

    for (let i = 0; i < count; i += 1) {
      const sprite = new Sprite(texture);
      sprite.anchor.set(0.5);
      sprite.rotation = rand() * Math.PI * 2;
      sprite.alpha = 0.22 + rand() * 0.58;
      const baseScale = 0.26 + rand() ** 0.72 * 2.05;
      sprite.scale.set(baseScale);
      sprite.position.set(rand() * regionW, rand() * regionH);
      sprite.eventMode = 'none';
      this.bgNameLayer.addChild(sprite);
      this.bgNameMotifs.push({
        sprite,
        vx: (rand() - 0.5) * 26 * speed,
        vy: (rand() - 0.5) * 26 * speed,
        rotVel: (rand() - 0.5) * 0.42 * speed,
        baseScale,
        driftPhase: rand() * Math.PI * 2,
        pulseRate: 0.2 + rand() * 0.65,
      });
    }
  }

  private resetMarkMotion(): void {
    this.endMarkDrag(undefined);
    this.markIntroActive = false;
    this.markIntroT = 0;
    this.markAttract01 = 0;
    this.setMarkPivot(0.5, 0.5, false);
    this.markLayer.position.set(this.markHome.x, this.markHome.y);
    this.mark.position.set(0, 0);
    this.mark.rotation = 0;
    this.markVel.x = 0;
    this.markVel.y = 0;
    this.markRotVel = 0;
  }

  /** Keep the pivot fixed in world space when changing anchor. */
  private setMarkPivot(ax: number, ay: number, preserveWorld: boolean): void {
    const w = Math.max(this.mark.width, 1);
    const h = Math.max(this.mark.height, 1);
    const ox = this.mark.anchor.x;
    const oy = this.mark.anchor.y;

    if (preserveWorld && (Math.abs(ox - ax) > 0.001 || Math.abs(oy - ay) > 0.001)) {
      const rot = this.mark.rotation;
      const cos = Math.cos(rot);
      const sin = Math.sin(rot);
      const wx = this.markLayer.x + ox * w * cos - oy * h * sin;
      const wy = this.markLayer.y + ox * w * sin + oy * h * cos;
      this.mark.anchor.set(ax, ay);
      this.markLayer.x = wx - (ax * w * cos - ay * h * sin);
      this.markLayer.y = wy - (ax * w * sin + ay * h * cos);
    } else {
      this.mark.anchor.set(ax, ay);
    }
    this.mark.position.set(0, 0);
  }

  private markLocalCorners(): Array<[number, number]> {
    const w = Math.max(this.mark.width, 1);
    const h = Math.max(this.mark.height, 1);
    const ax = this.mark.anchor.x;
    const ay = this.mark.anchor.y;
    return [
      [-ax * w, -ay * h],
      [(1 - ax) * w, -ay * h],
      [(1 - ax) * w, (1 - ay) * h],
      [-ax * w, (1 - ay) * h],
    ];
  }

  private markOffscreenStart(): { x: number; y: number; rot: number } {
    const padX = Math.max(this.mark.width * 0.5, 80);
    const padY = Math.max(this.mark.height * 0.65, 56);
    if (this.landscape) {
      return { x: -padX, y: this.h + padY, rot: 0.52 };
    }
    return { x: -padX * 0.35, y: -padY, rot: -0.38 };
  }

  private beginMarkIntro(): void {
    if (this.reducedMotion) {
      this.resetMarkMotion();
      return;
    }
    this.markIntroFrom = this.markOffscreenStart();
    this.markAttract01 = 0;
    this.setMarkPivot(0.5, 0.5, false);
    this.markLayer.position.set(this.markIntroFrom.x, this.markIntroFrom.y);
    this.mark.rotation = this.markIntroFrom.rot;
    this.markVel.x = 0;
    this.markVel.y = 0;
    this.markRotVel = 0;
    this.markIntroT = 0;
    this.markIntroActive = true;
  }

  /** Load entrance — returns true while the intro tween is running. */
  private updateMarkIntro(dt: number): boolean {
    if (!this.markIntroActive) return false;

    this.markIntroT += dt;
    const t = Math.min(1, this.markIntroT / this.markIntroDur);
    const e = 1 - Math.pow(1 - t, 3.35);
    const snap = t < 1 ? Math.sin(t * Math.PI) * 0.028 * (1 - t) : 0;
    const dx = this.markHome.x - this.markIntroFrom.x;
    const dy = this.markHome.y - this.markIntroFrom.y;

    this.markLayer.x = this.markIntroFrom.x + dx * e + dx * snap;
    this.markLayer.y = this.markIntroFrom.y + dy * e + dy * snap;
    this.mark.rotation = this.markIntroFrom.rot * (1 - e);

    if (t >= 1) {
      this.markIntroActive = false;
      this.resetMarkMotion();
    }
    return this.markIntroActive;
  }

  /** Furthest extent of rotated type toward the split from the pivot. */
  private markExtentTowardSplit(rotation: number): number {
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    const corners = this.markLocalCorners();
    if (this.landscape) {
      let maxX = -Infinity;
      for (const [lx, ly] of corners) {
        maxX = Math.max(maxX, lx * cos - ly * sin);
      }
      return maxX;
    }
    let maxY = -Infinity;
    for (const [lx, ly] of corners) {
      maxY = Math.max(maxY, lx * sin + ly * cos);
    }
    return maxY;
  }

  private clampMarkRotationToSplit(rotation: number, allowedExtent: number): number {
    if (allowedExtent <= 0) return 0;
    if (this.markExtentTowardSplit(rotation) <= allowedExtent) return rotation;
    let r = rotation;
    for (let i = 0; i < 10; i++) {
      if (this.markExtentTowardSplit(r) <= allowedExtent) return r;
      r *= 0.82;
    }
    return 0;
  }

  private topHeavyInertia(): number {
    const half = this.body.width * 0.5;
    return this.body.mass * half * half * 0.92;
  }

  /** Torque from a center-applied force when mass sits above the pivot. */
  private topHeavyTorqueFromForce(fx: number, fy: number): number {
    const off = this.body.width * 0.5 * this.topHeavyCom;
    const s = Math.sin(this.body.angle);
    const c = Math.cos(this.body.angle);
    const rx = off * s;
    const ry = -off * c;
    return rx * fy - ry * fx;
  }

  private applyLinearForceWithTopHeavy(fx: number, fy: number, dt: number): void {
    const invMass = 1 / this.body.mass;
    this.body.vx += fx * invMass * dt;
    this.body.vy += fy * invMass * dt;
    this.body.angularVelocity += (this.topHeavyTorqueFromForce(fx, fy) / this.topHeavyInertia()) * dt;
  }

  /**
   * Once the square crosses onto field B, top-heaviness makes it fall over —
   * upper half leans one way, lower half the other (pivoting on the split side).
   */
  private applyTopHeavySplitFall(dt: number): void {
    if (this.snapping || this.solved || this.holding) return;

    const cross = this.playerSplitCross01();
    if (cross <= 0.002) {
      this.body.angularVelocity += (0 - this.body.angle) * 5.5 * dt;
      return;
    }

    const crossEase = cross * cross * (3 - 2 * cross);
    const midY = this.h * 0.5;
    const yNorm = Math.max(-1, Math.min(1, (this.body.y - midY) / Math.max(1, this.h * 0.34)));
    const absY = Math.abs(yNorm);
    if (absY < 0.1) return;

    const boost = this.reducedMotion ? 0.55 : 1;
    const leanMax = 0.74 * boost;
    const tiltSign = yNorm < 0 ? 1 : -1;
    let tiltTarget = tiltSign * leanMax * crossEase * Math.min(1, absY * 1.1);

    if (this.lastAlign.inSoftField) {
      const softDist = Math.max(1, this.receiver.size * this.tuning.softFieldRatio);
      const softDepth = 1 - Math.min(1, this.lastAlign.centerDist / softDist);
      tiltTarget =
        this.receiver.angle + (tiltTarget - this.receiver.angle) * (1 - softDepth) * 0.45;
    }

    const pull = (140 + 220 * crossEase) * boost * dt;
    if (this.landscape) {
      this.body.angularVelocity += (this.topHeavyTorqueFromForce(pull, 0) / this.topHeavyInertia()) * dt;
    } else {
      this.body.angularVelocity += (this.topHeavyTorqueFromForce(0, pull) / this.topHeavyInertia()) * dt;
    }

    this.body.angularVelocity += (tiltTarget - this.body.angle) * (9 + crossEase * 13) * dt;
  }

  /** How far the pink square has crossed into field B (0 = home side, 1 = deep past split). */
  private playerSplitCross01(): number {
    const split = this.landscape ? this.tuning.splitRatio : 0.55;
    const half = this.body.width * 0.5;
    if (this.landscape) {
      const splitX = this.w * split;
      const depth = this.body.x + half - splitX;
      const span = Math.max(40, this.w - splitX);
      return Math.min(1, Math.max(0, depth / span));
    }
    const splitY = this.h * split;
    const depth = this.body.y + half - splitY;
    const span = Math.max(40, this.h - splitY);
    return Math.min(1, Math.max(0, depth / span));
  }

  private markHitSize(): { w: number; h: number } {
    const fs = this.markFontSize();
    const bounds = this.mark.getLocalBounds();
    return {
      w: Math.max(bounds.width, fs * 5.4, 140),
      h: Math.max(bounds.height, fs * 1.25, 32),
    };
  }

  private pointerInMarkHit(px: number, py: number, extra = 0): boolean {
    const { w, h } = this.markHitSize();
    const dx = px - this.markLayer.x;
    const dy = py - this.markLayer.y;
    const rot = -this.mark.rotation;
    const cos = Math.cos(rot);
    const sin = Math.sin(rot);
    const lx = dx * cos - dy * sin;
    const ly = dx * sin + dy * cos;
    const padX = w * 0.5 + extra;
    const padY = h * 0.5 + extra * 0.65;
    return Math.abs(lx) <= padX && Math.abs(ly) <= padY;
  }

  private tryBeginMarkDrag(e: PointerEvent): boolean {
    if (e.pointerType !== 'mouse' || e.button !== 0) return false;
    if (this.markIntroActive || this.snapping || this.solved || this.holding) return false;
    if (!this.pointerInMarkHit(this.pointer.x, this.pointer.y, 36)) return false;
    this.markDrag.active = true;
    this.markDrag.offsetX = this.pointer.x - this.markLayer.x;
    this.markDrag.offsetY = this.pointer.y - this.markLayer.y;
    this.markDrag.pointerId = e.pointerId;
    this.markVel.x = 0;
    this.markVel.y = 0;
    this.markRotVel = 0;
    try {
      this.app.canvas.setPointerCapture(e.pointerId);
    } catch {
      // ignore — capture optional
    }
    return true;
  }

  private endMarkDrag(e?: PointerEvent): void {
    if (!this.markDrag.active) return;
    const pid = this.markDrag.pointerId;
    this.markDrag.active = false;
    this.markDrag.pointerId = -1;
    this.markVel.x = 0;
    this.markVel.y = 0;
    if (pid >= 0) {
      try {
        if (this.app.canvas.hasPointerCapture(pid)) {
          this.app.canvas.releasePointerCapture(pid);
        }
      } catch {
        // ignore
      }
    }
    if (e) e.preventDefault();
  }

  /** Desktop — cursor nudges the mark when nearby (springs back when cursor leaves). */
  private applyMarkCursorNudge(dt: number, boost: number): void {
    if (this.pointer.isTouch || !this.pointer.inside || this.markDrag.active || this.markIntroActive) {
      return;
    }

    const dx = this.pointer.x - this.markLayer.x;
    const dy = this.pointer.y - this.markLayer.y;
    const dist = Math.hypot(dx, dy) || 1;
    const reach = Math.max(130, this.markFontSize() * 3.1);
    if (dist >= reach) return;

    const influence = (1 - dist / reach) ** 2;
    const pull = 240 * boost * influence;
    this.markVel.x += (dx / dist) * pull * dt;
    this.markVel.y += (dy / dist) * pull * dt;

    const sv = Math.hypot(this.pointer.svx, this.pointer.svy);
    if (sv > 24) {
      this.markVel.x += this.pointer.svx * 0.12 * influence * dt;
      this.markVel.y += this.pointer.svy * 0.12 * influence * dt;
    }
  }

  private clampMarkLayerPosition(): void {
    const split = this.landscape ? this.tuning.splitRatio : 0.55;
    const splitLine = this.landscape ? this.w * split : this.h * split;
    const textW = Math.max(this.mark.width, 1);
    const textH = Math.max(this.mark.height, 1);
    const margin = Math.max(10, (this.landscape ? textW : textH) * 0.08);
    const pad = Math.max(8, Math.min(textW, textH) * 0.32);
    const allowedExtent = Math.max(
      4,
      splitLine - margin - (this.landscape ? this.markLayer.x : this.markLayer.y),
    );
    const rotBefore = this.mark.rotation;
    this.mark.rotation = this.clampMarkRotationToSplit(this.mark.rotation, allowedExtent);
    if (Math.abs(this.mark.rotation - rotBefore) > 0.002) {
      this.markRotVel *= 0.25;
    }

    if (this.landscape) {
      const extent = this.markExtentTowardSplit(this.mark.rotation);
      const maxHingeX = splitLine - margin - extent;
      if (this.markLayer.x > maxHingeX) {
        this.markLayer.x = maxHingeX;
        this.markVel.x = Math.min(0, this.markVel.x * 0.08);
        this.markRotVel *= 0.65;
        this.mark.rotation = this.clampMarkRotationToSplit(
          this.mark.rotation,
          Math.max(4, splitLine - margin - this.markLayer.x),
        );
      }
      this.markLayer.x = Math.max(pad, this.markLayer.x);
      this.markLayer.y = Math.min(this.h - pad, Math.max(pad, this.markLayer.y));
    } else {
      const extent = this.markExtentTowardSplit(this.mark.rotation);
      const maxHingeY = splitLine - margin - extent;
      if (this.markLayer.y > maxHingeY) {
        this.markLayer.y = maxHingeY;
        this.markVel.y = Math.min(0, this.markVel.y * 0.08);
        this.markRotVel *= 0.65;
        this.mark.rotation = this.clampMarkRotationToSplit(
          this.mark.rotation,
          Math.max(4, splitLine - margin - this.markLayer.y),
        );
      }
      this.markLayer.y = Math.max(pad, this.markLayer.y);
      this.markLayer.x = Math.min(this.w - pad, Math.max(pad, this.markLayer.x));
    }
  }

  private updateMarkMotion(dt: number): void {
    if (this.updateMarkIntro(dt)) return;

    if (this.markDrag.active) {
      this.markLayer.x = this.pointer.x - this.markDrag.offsetX;
      this.markLayer.y = this.pointer.y - this.markDrag.offsetY;
      this.markVel.x = 0;
      this.markVel.y = 0;
      this.markRotVel *= Math.exp(-5.5 * dt);
      this.mark.rotation += this.markRotVel * dt;
      this.clampMarkLayerPosition();
      return;
    }

    const boost = this.reducedMotion ? 0.55 : 1;
    const split = this.landscape ? this.tuning.splitRatio : 0.55;
    const crossRaw = this.playerSplitCross01();
    const engage = crossRaw > this.markAttract01 ? 2.15 : 3.4;
    this.markAttract01 += (crossRaw - this.markAttract01) * Math.min(1, engage * dt);
    const cross = this.markAttract01 * this.markAttract01 * (3 - 2 * this.markAttract01);

    const spring = 98 * boost;
    const dampPow = Math.exp(-(6.8 + cross * 2.2) * dt);
    const splitLine = this.landscape ? this.w * split : this.h * split;
    const textW = Math.max(this.mark.width, 1);
    const textH = Math.max(this.mark.height, 1);
    const margin = Math.max(10, (this.landscape ? textW : textH) * 0.08);

    const pinkX = this.body.x;
    const pinkY = this.body.y;
    const wallCoord = splitLine - margin;

    let targetX = this.markHome.x;
    let targetY = this.markHome.y;
    if (this.landscape) {
      targetX = this.markHome.x + (wallCoord - this.markHome.x) * cross;
      targetY = this.markHome.y + (pinkY - this.markHome.y) * cross * 0.14;
    } else {
      targetY = this.markHome.y + (wallCoord - this.markHome.y) * cross;
      targetX = this.markHome.x + (pinkX - this.markHome.x) * cross * 0.14;
    }

    this.applyMarkCursorNudge(dt, boost);

    const pull = spring * (0.4 + cross * 0.6);
    this.markVel.x += (targetX - this.markLayer.x) * pull * dt;
    this.markVel.y += (targetY - this.markLayer.y) * pull * dt;

    const yNorm = Math.max(-1, Math.min(1, (pinkY - this.markHome.y) / Math.max(1, this.h * 0.34)));
    const absY = Math.abs(yNorm);
    const pinkDist = Math.hypot(pinkX - this.markLayer.x, pinkY - this.markLayer.y) || 1;
    const leanMag =
      cross > 0.001 ? Math.min(0.62, cross * (0.28 + Math.min(0.34, pinkDist / (this.h * 0.75)))) : 0;
    let tiltTarget = 0;
    if (absY > 0.08) {
      tiltTarget = (yNorm < 0 ? 1 : -1) * leanMag * Math.min(1, absY * 1.1);
    }

    const rotSpring = 11 * (1 - cross * 0.5) + (8 + cross * 10) * cross;
    this.markRotVel += (tiltTarget - this.mark.rotation) * rotSpring * dt;

    this.markVel.x *= dampPow;
    this.markVel.y *= dampPow;
    this.markRotVel *= Math.exp(-(3.2 + cross * 1.4) * dt);

    this.markLayer.x += this.markVel.x * dt;
    this.markLayer.y += this.markVel.y * dt;
    this.mark.rotation += this.markRotVel * dt;
    this.clampMarkLayerPosition();
  }

  private lerpColor(from: number, to: number, t: number): number {
    const k = Math.min(1, Math.max(0, t));
    const r = Math.round(((from >> 16) & 0xff) + (((to >> 16) & 0xff) - ((from >> 16) & 0xff)) * k);
    const g = Math.round(((from >> 8) & 0xff) + (((to >> 8) & 0xff) - ((from >> 8) & 0xff)) * k);
    const bl = Math.round((from & 0xff) + ((to & 0xff) - (from & 0xff)) * k);
    return (r << 16) | (g << 8) | bl;
  }

  private drawDynamic(): void {
    const b = this.body;
    const half = b.width * 0.5;
    const pink = this.palette.accent;
    const hotPink = 0xff78d4;
    const pulling =
      this.pointer.active && this.pointer.inside && !this.markDrag.active && !this.snapping && !this.solved;
    if (!pulling) {
      this.smoothPull01 *= 0.82;
    }

    this.playerGfx.clear();
    const t = performance.now() / 1000;
    const swollen = 1 - this.settle01;
    let drawScale = 1 + swollen * 0.12;
    if (this.isSettling && !this.reducedMotion) {
      drawScale *= 1 + 0.022 * Math.sin(t * 5 + this.calmT * 1.4);
    } else if (
      this.settle01 >= this.snapSettleMin &&
      this.lastAlign.inSoftField &&
      !this.snapping &&
      !this.solved &&
      !this.reducedMotion
    ) {
      drawScale *= 1 + 0.012 * Math.sin(t * 3.2);
    }

    const pull = this.smoothPull01;
    const glow = Math.max(0, Math.min(1, (pull - 0.28) / 0.62));
    const pulse = this.reducedMotion ? 1 : 1 + 0.035 * Math.sin(t * 11) * glow;

    if (glow > 0.01) {
      for (let i = 4; i >= 1; i -= 1) {
        const layer = glow * (1 - (i - 1) * 0.16) * pulse;
        const pad = half * i * 0.24 * glow;
        this.playerGfx
          .rect(-half - pad, -half - pad, b.width + pad * 2, b.height + pad * 2)
          .fill({ color: hotPink, alpha: 0.055 * layer });
      }
    }

    const fillPink = this.lerpColor(pink, hotPink, glow * 0.92);
    this.playerGfx.rect(-half, -half, b.width, b.height).fill(fillPink);

    if (glow > 0.08) {
      this.playerGfx
        .rect(-half, -half, b.width, b.height)
        .stroke({
          width: 1.2 + glow * 3.5,
          color: 0xffffff,
          alpha: 0.12 + glow * 0.38,
        });
      this.playerGfx
        .rect(-half, -half, b.width, b.height)
        .stroke({
          width: 0.8 + glow * 2,
          color: hotPink,
          alpha: 0.2 + glow * 0.55,
        });
    }

    if (glow > 0.45) {
      const core = (glow - 0.45) / 0.55;
      const inset = half * (1 - core * 0.42);
      this.playerGfx
        .rect(-inset, -inset, inset * 2, inset * 2)
        .fill({ color: 0xffffff, alpha: core * 0.22 * pulse });
    }

    const hitFlash = Math.max(this.rejectFlash, this.repulsorFlash);
    if (hitFlash > 0) {
      this.playerGfx
        .rect(-half, -half, b.width, b.height)
        .fill({ color: 0xffffff, alpha: hitFlash * 0.14 });
    }

    this.playerGfx.scale.set(drawScale);
    this.playerGfx.position.set(b.x, b.y);
    this.playerGfx.rotation = b.angle;

    const r = this.receiver;
    const align = this.lastAlign;
    const softDist = r.size * this.tuning.softFieldRatio;
    const snapDist = r.size * this.tuning.snapDistanceRatio;
    const closeEnough = align.centerDist <= softDist && !this.snapping && !this.solved;

    this.receiverGfx.clear();
    this.receiverGfx
      .rect(-r.size * 0.5, -r.size * 0.5, r.size, r.size)
      .fill(this.palette.fieldA);

    if (closeEnough) {
      const span = Math.max(1, softDist - snapDist);
      const proximity = 1 - Math.min(1, Math.max(0, align.centerDist - snapDist) / span);
      const strokeW = Math.max(2, Math.min(4, r.size * 0.045));
      const ready = this.settle01 >= this.snapSettleMin;
      this.receiverGfx
        .rect(-r.size * 0.5, -r.size * 0.5, r.size, r.size)
        .stroke({
          width: strokeW,
          color: this.palette.accent,
          alpha: proximity * (ready ? 0.98 : 0.55),
        });
      const inset = r.size * 0.5 * (1 - this.settle01) * 0.22;
      if (inset > 1.5) {
        this.receiverGfx
          .rect(-r.size * 0.5 + inset, -r.size * 0.5 + inset, r.size - inset * 2, r.size - inset * 2)
          .stroke({
            width: Math.max(1.25, strokeW * 0.75),
            color: 0xffffff,
            alpha: proximity * (1 - this.settle01) * 0.35,
          });
      }
    }

    this.receiverGfx.position.set(r.x, r.y);
    this.receiverGfx.rotation = r.angle;

    this.drawRepulsors();

    this.cursorGfx.clear();
    if (this.pointer.active && this.pointer.inside && !this.markDrag.active) {
      const canvasBox = this.app.canvas.getBoundingClientRect();
      const cx = canvasBox.left + (b.x / this.w) * canvasBox.width;
      const cy = canvasBox.top + (b.y / this.h) * canvasBox.height;
      const coupled = this.smoothPull01 > 0.48 && !this.pointer.isTouch;
      setCustomCursorCoupled(coupled, this.smoothPull01, cx, cy);
      if (!coupled) {
        setCustomCursorIntensity(
          this.lastForce01,
          this.lastForce01 > 0.02 || this.pointer.isTouch,
          this.pointer.isTouch,
        );
      }
    } else if (!this.pointer.isTouch) {
      setCustomCursorCoupled(false, 0);
      setCustomCursorIntensity(0, false);
    }
  }

  private updateRepulsors(dt: number): void {
    const interval = this.reducedMotion ? this.tuning.repulsorInterval * 1.6 : this.tuning.repulsorInterval;
    this.repulsorSpawnT -= dt;
    if (this.repulsorSpawnT <= 0) {
      this.spawnRepulsor();
      this.repulsorSpawnT = interval * (0.85 + Math.random() * 0.35);
    }

    for (const r of this.repulsors) {
      r.age += dt;
      // Slight grow while alive — reads as a pressure wave.
      r.radius += dt * 18;
    }
    this.repulsors = this.repulsors.filter((r) => r.age < r.life);
  }

  private spawnRepulsor(): void {
    // Place between the square and the receiver — blocks the natural approach path.
    const dx = this.receiver.x - this.body.x;
    const dy = this.receiver.y - this.body.y;
    const dist = Math.hypot(dx, dy) || 1;
    const along = 0.28 + Math.random() * 0.42;
    const side = (Math.random() - 0.5) * this.tuning.repulsorRadius * 0.9;
    const nx = -dy / dist;
    const ny = dx / dist;
    let x = this.body.x + dx * along + nx * side;
    let y = this.body.y + dy * along + ny * side;

    // Keep on-screen and off the receiver itself.
    const margin = this.tuning.repulsorRadius * 0.35;
    x = Math.min(this.w - margin, Math.max(margin, x));
    y = Math.min(this.h - margin, Math.max(margin, y));
    if (Math.hypot(x - this.receiver.x, y - this.receiver.y) < this.receiver.size * 1.1) {
      x = this.body.x + dx * 0.35;
      y = this.body.y + dy * 0.35;
    }

    const strength = this.reducedMotion
      ? this.tuning.repulsorStrength * 0.55
      : this.tuning.repulsorStrength;

    this.repulsors.push({
      x,
      y,
      age: 0,
      life: this.tuning.repulsorLife,
      radius: this.tuning.repulsorRadius * (0.85 + Math.random() * 0.25),
      strength,
    });

    // Cap concurrent antagonists so the scene stays readable.
    if (this.repulsors.length > 3) this.repulsors.shift();
  }

  private drawRepulsors(): void {
    this.repulsorGfx.clear();
    for (const r of this.repulsors) {
      const lifeT = r.age / r.life;
      const alpha = (lifeT < 0.2 ? lifeT / 0.2 : 1 - (lifeT - 0.2) / 0.8) * 0.55;
      const stroke = Math.max(2, r.radius * 0.035);
      // Flat ring only — antagonist mark, not a glow.
      this.repulsorGfx.circle(r.x, r.y, r.radius).stroke({
        width: stroke,
        color: this.palette.accent,
        alpha: Math.max(0.05, alpha),
      });
      // Inner quieter ring
      this.repulsorGfx.circle(r.x, r.y, r.radius * 0.42).stroke({
        width: Math.max(1.25, stroke * 0.7),
        color: this.palette.accent,
        alpha: Math.max(0.03, alpha * 0.55),
      });
    }
  }

  /**
   * Rough handling swells the square; resting near the hole lets it settle back to size.
   * Only drains on clearly rough motion — not normal guiding.
   */
  private updateSettle(dt: number): void {
    if (this.solved || this.snapping || this.holding) return;

    const speed = Math.hypot(this.body.vx, this.body.vy);
    const pointerSpeed = Math.hypot(this.pointer.svx, this.pointer.svy);
    const spin = Math.abs(this.body.angularVelocity);
    const inSoft = this.lastAlign.inSoftField;
    const softDist = Math.max(1, this.receiver.size * this.tuning.softFieldRatio);
    const softDepth = inSoft ? 1 - Math.min(1, this.lastAlign.centerDist / softDist) : 0;

    const touch = this.pointer.isTouch;
    let rough = 0;
    if (this.pointer.active && this.pointer.inside) {
      const forceGate = touch ? 0.68 : 0.55;
      const speedGate = touch ? 520 : 340;
      if (this.lastForce01 > forceGate) rough += (this.lastForce01 - (forceGate - 0.1)) * 0.65;
      if (pointerSpeed > speedGate) rough += Math.min(1, (pointerSpeed - speedGate) / 480) * 0.48;
    }
    if (speed > (touch ? 260 : 220)) rough += Math.min(1, (speed - (touch ? 260 : 220)) / 400) * 0.42;
    const cross = this.playerSplitCross01();
    const spinGate = cross > 0.08 ? (touch ? 7 : 6.2) : touch ? 5 : 4.2;
    if (spin > spinGate) rough += Math.min(1, (spin - spinGate) / 5) * 0.38;

    const released = !this.pointer.active;
    const gentleTouch =
      this.pointer.active &&
      this.pointer.inside &&
      this.lastForce01 < (touch ? 0.78 : 0.5) &&
      pointerSpeed < (touch ? 420 : 300);
    const touchHold =
      touch &&
      this.pointer.active &&
      this.pointer.inside &&
      inSoft &&
      pointerSpeed < 160 &&
      this.lastForce01 < 0.88;
    const still = speed < (touch ? 185 : 150) && spin < (touch ? 4.5 : 3.4);
    const veryStill = speed < (touch ? 100 : 70) && spin < (touch ? 2.6 : 1.8);

    let recover = 0;

    if (rough >= 0.22) {
      this.settle01 = Math.max(0, this.settle01 - rough * 0.95 * dt);
      this.calmT = rough >= 0.45 ? 0 : Math.max(0, this.calmT - dt * 1.5);
      this.isSettling = false;
      return;
    }

    if (inSoft && still && (released || gentleTouch || touchHold)) {
      this.calmT += dt;
      const ramp = Math.min(1.8, this.calmT * 0.85);
      recover = (0.48 + softDepth * 0.62 + ramp * 0.38) * dt;
      if (touchHold && veryStill) recover *= 1.75;
      else if (released && veryStill) recover *= 1.6;
      else if (touchHold) recover *= 1.35;
    } else if (released && veryStill) {
      this.calmT += dt * 0.55;
      recover = (0.18 + Math.min(0.45, this.calmT * 0.28)) * dt;
    } else if (inSoft && still) {
      this.calmT += dt * 0.35;
      recover = (0.22 + softDepth * 0.3) * dt;
    }

    if (recover > 0) {
      const before = this.settle01;
      this.settle01 = Math.min(1, this.settle01 + recover);
      this.isSettling = this.settle01 < 0.98 && this.settle01 > before + 0.0001;
    } else if (rough > 0) {
      this.settle01 = Math.max(0, this.settle01 - rough * 0.55 * dt);
      this.calmT = Math.max(0, this.calmT - dt * 0.6);
      this.isSettling = false;
    } else {
      this.calmT = Math.max(0, this.calmT - dt * 0.4);
      this.isSettling = false;
    }
  }

  private applySnapReject(settleMin: number): void {
    const dx = this.body.x - this.receiver.x;
    const dy = this.body.y - this.receiver.y;
    const d = Math.hypot(dx, dy) || 1;
    const gap = Math.max(0, settleMin - this.settle01);
    const kick = (this.reducedMotion ? 22 : 38) * (0.55 + gap * 0.9);
    this.body.vx += (dx / d) * kick;
    this.body.vy += (dy / d) * kick;
    this.body.angularVelocity += 0.85 * (Math.random() > 0.5 ? 1 : -1);
    this.settle01 = Math.max(0, this.settle01 - 0.1);
    this.rejectFlash = 0.55;
    this.snapRejectCooldown = 0.55;
  }

  private beginSnap(): void {
    this.snapping = true;
    this.snapT = 0;
    this.snapFrom = { x: this.body.x, y: this.body.y, angle: this.body.angle };
    this.body.vx = 0;
    this.body.vy = 0;
    this.body.angularVelocity = 0;
    this.hideHint();
  }

  private updateSnap(dt: number): void {
    this.snapT += dt;
    const dur = this.reducedMotion ? 0.2 : this.tuning.snapDuration;
    const t = Math.min(1, this.snapT / dur);
    const e = 1 - Math.pow(1 - t, 3);
    this.body.x = lerp(this.snapFrom.x, this.receiver.x, e);
    this.body.y = lerp(this.snapFrom.y, this.receiver.y, e);
    this.body.angle = this.snapFrom.angle + shortest(this.snapFrom.angle, this.receiver.angle) * e;

    if (t >= 1) {
      this.solved = true;
      this.snapping = false;
      this.holding = true;
      this.holdT = 0;
      this.body.x = this.receiver.x;
      this.body.y = this.receiver.y;
      this.body.angle = this.receiver.angle;
    }
  }

  private bindPointer(): void {
    const el = this.app.canvas;
    el.addEventListener('pointerdown', this.onPointerDown);
    el.addEventListener('pointermove', this.onPointerMove);
    el.addEventListener('pointerup', this.onPointerUp);
    el.addEventListener('pointercancel', this.onPointerUp);
    el.addEventListener('pointerleave', this.onPointerLeave);
    el.addEventListener('pointerenter', this.onPointerEnter);
    el.addEventListener('contextmenu', this.onContextMenu);
  }

  private unbindPointer(): void {
    const el = this.app.canvas;
    el.removeEventListener('pointerdown', this.onPointerDown);
    el.removeEventListener('pointermove', this.onPointerMove);
    el.removeEventListener('pointerup', this.onPointerUp);
    el.removeEventListener('pointercancel', this.onPointerUp);
    el.removeEventListener('pointerleave', this.onPointerLeave);
    el.removeEventListener('pointerenter', this.onPointerEnter);
    el.removeEventListener('contextmenu', this.onContextMenu);
  }

  private onContextMenu = (e: Event): void => {
    e.preventDefault();
  };

  private onPointerDown = (e: PointerEvent): void => {
    if (e.button !== 0) return;

    this.pointer.isTouch = e.pointerType === 'touch';
    this.pointer.inside = true;
    this.setPointer(e, true);
    this.pointer.active = true;

    if (this.tryBeginMarkDrag(e)) {
      return;
    }

    if (this.pointer.isTouch) {
      setCustomCursorClientPos(e.clientX, e.clientY, { touch: true, pressed: true });
      e.preventDefault();
    }
  };

  private onPointerMove = (e: PointerEvent): void => {
    this.pointer.isTouch = e.pointerType === 'touch';
    this.pointer.inside = true;
    if (e.pointerType === 'mouse' || this.pointer.active) {
      this.setPointer(e, false);
      this.pointer.active = true;
    }
    if (
      !this.markDrag.active &&
      e.pointerType === 'mouse' &&
      (e.buttons & 1) !== 0 &&
      !this.markIntroActive &&
      !this.snapping &&
      !this.solved &&
      !this.holding &&
      this.pointerInMarkHit(this.pointer.x, this.pointer.y, 48)
    ) {
      this.markDrag.active = true;
      this.markDrag.offsetX = this.pointer.x - this.markLayer.x;
      this.markDrag.offsetY = this.pointer.y - this.markLayer.y;
      this.markDrag.pointerId = e.pointerId;
      this.markVel.x = 0;
      this.markVel.y = 0;
      this.markRotVel = 0;
      try {
        this.app.canvas.setPointerCapture(e.pointerId);
      } catch {
        // ignore
      }
    }
    if (this.pointer.isTouch && this.pointer.active) e.preventDefault();
  };

  private onPointerUp = (e: PointerEvent): void => {
    this.endMarkDrag(e);
    if (e.pointerType === 'touch') {
      this.pointer.active = false;
      setCustomCursorClientPos(e.clientX, e.clientY, { touch: true, pressed: false });
      setCustomCursorIntensity(0, false, true);
    } else {
      this.setPointer(e, false);
      this.pointer.active = true;
    }
  };

  private onPointerLeave = (): void => {
    this.endMarkDrag(undefined);
    this.pointer.inside = false;
    this.pointer.active = false;
    setCustomCursorIntensity(0, false, this.pointer.isTouch);
    if (this.pointer.isTouch) {
      setCustomCursorClientPos(0, 0, { touch: true, pressed: false });
    }
  };

  private onPointerEnter = (e: PointerEvent): void => {
    this.pointer.inside = true;
    if (e.pointerType === 'mouse') {
      this.setPointer(e, true);
      this.pointer.active = true;
    }
  };

  private setPointer(e: PointerEvent, resetVel: boolean): void {
    const rect = this.app.canvas.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * this.w;
    const y = ((e.clientY - rect.top) / rect.height) * this.h;
    const now = performance.now();

    if (resetVel || this.pointer.lastTs <= 0) {
      this.pointer.svx = 0;
      this.pointer.svy = 0;
    } else {
      const dt = Math.min(0.05, Math.max(0.001, (now - this.pointer.lastTs) / 1000));
      const rawVx = (x - this.pointer.x) / dt;
      const rawVy = (y - this.pointer.y) / dt;
      const a = this.tuning.pointerVelSmooth;
      this.pointer.svx += (rawVx - this.pointer.svx) * a;
      this.pointer.svy += (rawVy - this.pointer.svy) * a;
    }

    this.pointer.lastTs = now;
    this.pointer.x = x;
    this.pointer.y = y;
    setCustomCursorClientPos(e.clientX, e.clientY, {
      touch: e.pointerType === 'touch',
      pressed: this.pointer.active,
    });
  }

  private markInteracted(): void {
    if (this.interacted) return;
    this.interacted = true;
    this.hideHint();
    this.guideEl?.classList.add('is-dismissed');
  }

  private showHint(text: string): void {
    if (!this.hintEl) return;
    this.hintEl.hidden = false;
    this.hintEl.textContent = text;
  }

  private hideHint(): void {
    if (!this.hintEl) return;
    this.hintEl.hidden = true;
  }
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function shortest(from: number, to: number): number {
  let d = (to - from) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return d;
}

function hashSeed(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function mulberry32(a: number): () => number {
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
